import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import APP_DROPDOWNS from '../utils/ddlRegistry';
import {
  subscribeToDDLCollections,
  subscribeToDropdownRegistrations,
  saveDropdownRegistrations,
  getCollectionDepthTag,
} from '../utils/storage';
import { getPermissions } from '../utils/permissions';
import AccessDeniedModal from './AccessDeniedModal';
import './RegisterDropdownsPage.css';

/**
 * RegisterDropdownsPage — slide-in panel from the right
 *
 * Assign DDL collections to app dropdowns, configure relationships:
 *   None  — no relationship (Point To disabled)
 *   Link  — mutual exclusion between two dropdowns (always enabled)
 *   Chain — dependent dropdown showing sub-items of another dropdown's selection
 *            (only enabled when the assigned collection has Secondary/Tertiary depth)
 *
 * Link is bidirectional: selecting a link partner updates both rows.
 * Save validates link pairs share the same collection, and chain targets have nested items.
 */
export default function RegisterDropdownsPage({ onClose }) {
  const [collections, setCollections] = useState([]);
  const [configs, setConfigs]         = useState({});  // persisted from Firestore
  const [dirty, setDirty]             = useState({});  // unsaved local changes
  const [saving, setSaving]           = useState(false);
  const [saveError, setSaveError]     = useState('');
  const [showSaveDenied, setShowSaveDenied] = useState(false);

  const { userProfile } = useAuth();
  const perms = getPermissions(userProfile?.roles || []);

  useEffect(() => {
    const unsub1 = subscribeToDDLCollections(setCollections);
    const unsub2 = subscribeToDropdownRegistrations(setConfigs);
    return () => { unsub1(); unsub2(); };
  }, []);

  // ── Helpers ──────────────────────────────────────────────────────

  /** Get effective row: persisted config overridden by local dirty state */
  function getRow(ddId) {
    const base = configs[ddId] || { collectionId: '', relationship: 'none', pointTo: '' };
    return { ...base, ...dirty[ddId] };
  }

  function emptyRow() {
    return { collectionId: '', relationship: 'none', pointTo: '' };
  }

  /**
   * Chain button is only available when the assigned collection has nested items
   * (Secondary or Tertiary depth).
   */
  function isChainAvailable(ddId) {
    const row = getRow(ddId);
    if (!row.collectionId) return false;
    const col = collections.find((c) => c.id === row.collectionId);
    if (!col) return false;
    const depth = getCollectionDepthTag(col.items || []);
    return depth === 'Secondary' || depth === 'Tertiary';
  }

  // ── Dirty update with relationship side effects ──────────────────

  function updateDirty(ddId, field, value) {
    setDirty((prev) => {
      const newDirty = { ...prev };

      // Helper to get current effective row (merging prev dirty)
      const effective = (id) => ({
        ...(configs[id] || emptyRow()),
        ...prev[id],
      });

      const currentRow = effective(ddId);

      if (field === 'relationship') {
        // ── Switching relationship ────────────────────────────────
        const prevRel = currentRow.relationship || 'none';

        // When leaving 'link': clear the linked partner's back-reference
        if (prevRel === 'link' && currentRow.pointTo) {
          const linkedId  = currentRow.pointTo;
          const linkedRow = effective(linkedId);
          if (linkedRow.relationship === 'link' && linkedRow.pointTo === ddId) {
            newDirty[linkedId] = { ...linkedRow, relationship: 'none', pointTo: '' };
          }
        }

        // Clear pointTo when switching to 'none'
        const newPointTo = value === 'none' ? '' : currentRow.pointTo;
        newDirty[ddId] = { ...currentRow, relationship: value, pointTo: newPointTo };

      } else if (field === 'pointTo') {
        // ── Changing Point To ─────────────────────────────────────
        const currentRel  = currentRow.relationship || 'none';
        const prevPointTo = currentRow.pointTo || '';

        // If was linked to someone else, clear that partner's reference
        if (currentRel === 'link' && prevPointTo && prevPointTo !== value) {
          const prevLinked = effective(prevPointTo);
          if (prevLinked.relationship === 'link' && prevLinked.pointTo === ddId) {
            newDirty[prevPointTo] = { ...prevLinked, relationship: 'none', pointTo: '' };
          }
        }

        newDirty[ddId] = { ...currentRow, pointTo: value };

        // Link is bidirectional: also update the new partner
        if (currentRel === 'link' && value) {
          const newLinked = effective(value);
          newDirty[value] = { ...newLinked, relationship: 'link', pointTo: ddId };
        }

      } else if (field === 'collectionId') {
        // ── Changing assigned collection ──────────────────────────
        const newCol   = collections.find((c) => c.id === value);
        const newDepth = newCol ? getCollectionDepthTag(newCol.items || []) : 'Primary';
        const chainOk  = newDepth === 'Secondary' || newDepth === 'Tertiary';

        let updated = { ...currentRow, collectionId: value };

        // Auto-clear chain if new collection doesn't support it
        if (currentRow.relationship === 'chain' && !chainOk) {
          updated = { ...updated, relationship: 'none', pointTo: '' };
        }

        // Auto-clear link partner if collection changes while linked
        if (currentRow.relationship === 'link' && currentRow.pointTo) {
          const linkedId  = currentRow.pointTo;
          const linkedRow = effective(linkedId);
          if (linkedRow.relationship === 'link' && linkedRow.pointTo === ddId) {
            newDirty[linkedId] = { ...linkedRow, relationship: 'none', pointTo: '' };
          }
          updated = { ...updated, relationship: 'none', pointTo: '' };
        }

        newDirty[ddId] = updated;

      } else {
        newDirty[ddId] = { ...currentRow, [field]: value };
      }

      return newDirty;
    });
  }

  // ── Save with validation ──────────────────────────────────────────

  async function handleSave() {
    if (saving) return;
    if (!perms.canSaveDDLRegister) {
      setShowSaveDenied(true);
      return;
    }
    setSaveError('');

    // Build fully merged config
    const merged = { ...configs };
    for (const ddId of Object.keys(dirty)) {
      merged[ddId] = getRow(ddId);
    }

    const errors = [];

    // Validate link pairs: must share the same collection
    const checkedLinks = new Set();
    for (const [ddId, cfg] of Object.entries(merged)) {
      if (cfg.relationship !== 'link' || !cfg.pointTo || checkedLinks.has(ddId)) continue;
      checkedLinks.add(ddId);
      checkedLinks.add(cfg.pointTo);

      const partner = merged[cfg.pointTo];
      if (!partner || partner.collectionId !== cfg.collectionId) {
        const aLabel = APP_DROPDOWNS.find((d) => d.id === ddId)?.label        || ddId;
        const bLabel = APP_DROPDOWNS.find((d) => d.id === cfg.pointTo)?.label || cfg.pointTo;
        errors.push(
          `Link error: "${aLabel}" and "${bLabel}" must be assigned to the same collection.`
        );
      }
    }

    // Validate chain: pointed-to dropdown's collection must have nested items
    for (const [ddId, cfg] of Object.entries(merged)) {
      if (cfg.relationship !== 'chain' || !cfg.pointTo) continue;
      const targetCfg = merged[cfg.pointTo];
      if (targetCfg?.collectionId) {
        const targetCol   = collections.find((c) => c.id === targetCfg.collectionId);
        const targetDepth = targetCol ? getCollectionDepthTag(targetCol.items || []) : 'Primary';
        if (targetDepth === 'Primary') {
          const aLabel = APP_DROPDOWNS.find((d) => d.id === ddId)?.label           || ddId;
          const bLabel = APP_DROPDOWNS.find((d) => d.id === cfg.pointTo)?.label    || cfg.pointTo;
          errors.push(
            `Chain error: "${aLabel}" is chained to "${bLabel}", but that list is flat (no sub-items). ` +
            `Chain requires a list with nested values (Secondary or Tertiary depth).`
          );
        }
      }
    }

    if (errors.length > 0) {
      setSaveError(errors.join('\n'));
      return;
    }

    setSaving(true);
    try {
      await saveDropdownRegistrations(merged);
      setConfigs(merged);   // optimistic: adopt saved data immediately so UI doesn't flash
      setDirty({});
    } catch (err) {
      console.error('Failed to save registrations:', err);
      setSaveError('Save failed. Please try again.');
    }
    setSaving(false);
  }

  const hasDirty = Object.keys(dirty).length > 0;

  // ── Render ───────────────────────────────────────────────────────

  return (
    <div className="reg-overlay" onClick={onClose}>
      <div className="reg-panel" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="reg-header">
          <div>
            <h2 className="reg-title">Register Dropdowns</h2>
            <p className="reg-subtitle">Assign DDL lists to app dropdowns and configure relationships.</p>
          </div>
          <button className="reg-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Table */}
        <div className="reg-table-wrapper">
          <table className="reg-table">
            <thead>
              <tr>
                <th className="reg-col-name">Dropdown</th>
                <th className="reg-col-list">Assign Collection</th>
                <th className="reg-col-rel">Relationship</th>
                <th className="reg-col-point">Point To</th>
              </tr>
            </thead>
            <tbody>
              {APP_DROPDOWNS.map((dd) => {
                const row             = getRow(dd.id);
                const chainEnabled    = isChainAvailable(dd.id);
                const pointToDisabled = !row.relationship || row.relationship === 'none';
                const otherDropdowns  = APP_DROPDOWNS.filter((d) => d.id !== dd.id);

                return (
                  <tr
                    key={dd.id}
                    className={dirty[dd.id] ? 'reg-row reg-row-dirty' : 'reg-row'}
                  >
                    {/* Dropdown name */}
                    <td className="reg-col-name">
                      <span className="reg-dd-label">{dd.label}</span>
                      <span className="reg-dd-id">{dd.id}</span>
                    </td>

                    {/* Assign List — always assigns primary level of collection */}
                    <td className="reg-col-list">
                      <select
                        className={`reg-select${!row.collectionId ? ' reg-select-warn' : ''}`}
                        value={row.collectionId || ''}
                        onChange={(e) => updateDirty(dd.id, 'collectionId', e.target.value)}
                      >
                        <option value="">— None —</option>
                        {[...collections]
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                      </select>
                    </td>

                    {/* Relationship: Link | Chain | None */}
                    <td className="reg-col-rel">
                      <div className="reg-rel-group">

                        {/* Link — always enabled, bidirectional mutual exclusion */}
                        <button
                          className={`reg-rel-btn${row.relationship === 'link' ? ' active' : ''}`}
                          title="Link — mutually exclusive options between two dropdowns"
                          onClick={() => updateDirty(dd.id, 'relationship', 'link')}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 14c1-5 6-8 11-6" />
                            <polyline points="11 5 14 8 11 11" />
                            <path d="M21 10c-1 5-6 8-11 6" />
                            <polyline points="13 13 10 16 13 19" />
                          </svg>
                        </button>

                        {/* Chain — only enabled when collection has Secondary/Tertiary depth */}
                        <button
                          className={`reg-rel-btn${row.relationship === 'chain' ? ' active' : ''}`}
                          title={
                            chainEnabled
                              ? 'Chain — dependent dropdown (shows sub-items of Point To selection)'
                              : row.collectionId
                                ? 'Chain unavailable: assigned list is flat (no nested items)'
                                : 'Chain unavailable: assign a list first'
                          }
                          disabled={!chainEnabled}
                          onClick={() => chainEnabled && updateDirty(dd.id, 'relationship', 'chain')}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                          </svg>
                        </button>

                        {/* None — always enabled */}
                        <button
                          className={`reg-rel-btn${!row.relationship || row.relationship === 'none' ? ' active' : ''}`}
                          title="None — no relationship"
                          onClick={() => updateDirty(dd.id, 'relationship', 'none')}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                          </svg>
                        </button>
                      </div>
                    </td>

                    {/* Point To — disabled when relationship is None */}
                    <td className="reg-col-point">
                      <select
                        className="reg-select"
                        value={row.pointTo || ''}
                        onChange={(e) => updateDirty(dd.id, 'pointTo', e.target.value)}
                        disabled={pointToDisabled}
                      >
                        <option value="">— None —</option>
                        {[...otherDropdowns]
                          .sort((a, b) => a.label.localeCompare(b.label))
                          .map((d) => (
                            <option key={d.id} value={d.id}>{d.label}</option>
                          ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Save errors */}
        {saveError && (
          <div className="reg-save-error">
            {saveError.split('\n').map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="reg-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || !hasDirty}
          >
            {saving ? 'Saving…' : hasDirty ? 'Save Changes' : 'Saved'}
          </button>
        </div>

      </div>

      {showSaveDenied && (
        <AccessDeniedModal
          roles={userProfile?.roles || []}
          onClose={() => setShowSaveDenied(false)}
        />
      )}
    </div>
  );
}
